/* eslint-disable max-lines */

import { Component, effect, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import mixpanel from 'mixpanel-browser';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { catchError, combineLatest, EMPTY, filter, of, Subject, zip } from 'rxjs';
import { combineLatestWith, switchMap, takeUntil } from 'rxjs/operators';
import { UserDetails } from '@bn/api-interfaces';
import { AuthRoutes } from '@bn/auth';
import { AuthService } from '@bn/auth/services';
import { AppRoutes, ErrorsRoutes } from '@bn/dashboard-core/enums';
import { UserSettingsApiService } from '@bn/dashboard-shared/services';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { ApplicationRoles, BillingType, OutdatedApplicationRoles } from '@bn/shared/entities/enums';
import { ClickActionEvent, StringTMap } from '@bn/shared/entities/index';
import { DateTimeUtils } from '@bn/shared/utils';
import { environment } from '@shared/environments';
import {
  ExpiredTrialModalComponent,
  ExtendTrialAllUsersModalComponent,
  ExtendTrialModalComponent,
  PermissionsUpdateModalComponent,
  UserDisabledModalComponent,
} from './modals';
import { BillingUtils } from './pages/account/utils';

@Component({
  selector: 'bn-dashboard-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent extends AbstractLazyDirective implements OnDestroy, OnInit {
  private readonly authService = inject(AuthService);
  private readonly cookieService = inject(CookieService);
  private readonly dialog = inject(BsModalService);
  private readonly userSettingsApiService = inject(UserSettingsApiService);

  #translate = inject(TranslateService);

  public title = 'BunnyDashboard';
  public preloadImages!: string[];
  private readonly authStorageKey: string;
  // private userSettings!: UserSettings;
  private userDetails!: UserDetails;
  private queryParams?: Params;
  private stopModalSubscription = new Subject<boolean>();
  private showModalsQue: StringTMap<boolean> = {
    ['0_extendTrialModal']: false,
    ['1_extendTrialAllUsersModal']: false,
    ['2_expiredTrialModal']: false,
    ['3_permissionsModal']: false,
    ['4_userDisabled']: false,
  };
  #anyModalShown = signal(false);

  constructor() {
    super();

    this.#translate.addLangs(['en']);
    this.#translate.setFallbackLang('en');
    this.#translate.use('en');

    this.authEventsSubscriptions();

    this.authStorageKey = this.authService.getAuthStoreKey();
    this.preloadImages = this.contentService.getImagesForPreload();

    this.saveUtmDataToCookies();
    this.listenToAnyModalShown();
  }

  public ngOnInit() {
    mixpanel.init(environment.mixPanelToken, {
      debug: !environment.production, // enable debug log in console
      ignore_dnt: !environment.production, // override do not track if not production
      api_host: 'https://api-eu.mixpanel.com',
    });
    this.subscriptions.add(
      this.route.queryParams.subscribe(() => {
        this.hideLoadingScreen();
      }),
    );
  }

  @HostListener('window:storage', ['$event'])
  public localStorageChanged(event: StorageEvent) {
    if (event.key !== this.authStorageKey || (!event.oldValue && !!event.newValue) || (event.oldValue && event.newValue)) return;

    window.location.reload();
  }

  @HostListener('document:visibilitychange')
  public onFocus(): void {
    this.authService.onBrowserTabFocus();
  }

  private authEventsSubscriptions() {
    this.subscriptions.add(
      this.authService
        .getLoginSuccess$()
        .pipe(
          switchMap(() =>
            combineLatest([
              this.userDetailsService.getFreshUserDetails(),
              this.userSettingsApiService.loadUserSettings(),
              this.userDetailsService
                .getMarketingDetails()
                .pipe(catchError(() => of({ PkButtonUrl: '', PkCampaign: '', PkSource: '', PkMedium: '', PkKeyword: '', PkDevice: '', PkCid: '' }))),
            ]),
          ),
        )
        .subscribe(([userDetails, userSettings, marketingDetails]) => {
          if (!userDetails) throw new Error('User Details are missing after successful login');
          if (!userSettings) throw new Error('User Settings are missing after successful login');

          // this.userSettings = userSettings;
          this.userDetails = userDetails;

          mixpanel.register({
            PkButtonURL: marketingDetails.PkButtonUrl || '',
            PkCampaign: marketingDetails.PkCampaign || '',
            PkSource: marketingDetails.PkSource || '',
            PkMedium: marketingDetails.PkMedium || '',
            PkKeyword: marketingDetails.PkKeyword || '',
            PkDevice: marketingDetails.PkDevice || '',
            PkCid: marketingDetails.PkCid || '',
          });

          // Note: "isWelcome" is set by google SSO when users register
          const isWelcome = this.route.snapshot.queryParams?.isWelcome === 'True';
          const redirectUrlQueryParam = this.route.snapshot.queryParams['returnUrl'];
          // Note: SimpleRegisterPool is before welcome page and it will redirects users to welcome anyway
          const redirectUrl = isWelcome ? AppRoutes.SimpleRegisterPoll : redirectUrlQueryParam || null;

          if (redirectUrl) this.router.navigateByUrl(redirectUrl);

          // This is already handled in router events pipe
          // if (this.shouldShowExtendTrialModal()) {
          //   this.showModalsQue['0_extendTrialModal'] = true;
          // } else if (this.shouldShowExtendTrialAllUsersModal()) {
          //   this.showModalsQue['1_extendTrialAllUsersModal'] = true;
          // }

          // if (this.shouldShowExpiredTrialModal()) {
          //   this.showModalsQue['2_expiredTrialModal'] = true;
          // }

          if (this.shouldShowPermissionsModal()) {
            this.showModalsQue['3_permissionsModal'] = true;
          }

          this.handleShowModals();
        }),
    );

    this.subscriptions.add(
      this.authService.getLogoutSuccess$().subscribe((logoutData) => {
        window.location.replace(`/${AppRoutes.Auth}/${AuthRoutes.Login}` + (logoutData ? `?error=${logoutData}` : ''));
      }),
    );

    this.subscriptions.add(
      zip([
        this.userDetailsService.getUserDetails().pipe(
          filter((userDetails) => !!userDetails?.Email),
          switchMap((userDetails) => (userDetails ? of(userDetails.Email) : EMPTY)),
        ),
        this.route.queryParams.pipe(filter(({ user }) => !!user)),
      ]).subscribe(([loggedUserEmail, { user }]) => {
        if (loggedUserEmail === user) return;

        this.router.navigate([AppRoutes.Errors, ErrorsRoutes.UserMismatch], {
          queryParams: {
            user: this.route.snapshot.queryParams.user || undefined, // NOTE: email should be url encoded
            continueNavigation: this.router.routerState.snapshot.url,
          },
        });
      }),
    );

    this.subscriptions.add(
      this.router.events
        .pipe(
          combineLatestWith(this.userDetailsService.getUserDetails(), this.userSettingsApiService.getUserSettings$(), this.route.queryParams),
          takeUntil(this.stopModalSubscription),
        )
        .subscribe(([routerEvent, userDetails, _userSettings, queryParams]) => {
          if (routerEvent instanceof NavigationEnd) {
            this.queryParams = queryParams;
            if (userDetails) this.userDetails = userDetails;
            // if (userSettings) this.userSettings = _userSettings;

            if (this.shouldShowExtendTrialModal()) {
              this.showModalsQue['0_extendTrialModal'] = true;
            } else if (this.shouldShowExtendTrialAllUsersModal()) {
              this.showModalsQue['1_extendTrialAllUsersModal'] = true;
            }

            if (this.shouldShowExpiredTrialModal()) {
              this.showModalsQue['2_expiredTrialModal'] = true;
            }

            if (this.shouldShowNegativeBalanceModal()) {
              this.showModalsQue['4_userDisabled'] = true;
            }

            this.handleShowModals();

            this.stopModalSubscription.next(true);
            this.stopModalSubscription.complete();
          }
        }),
    );
  }

  private shouldShowExtendTrialAllUsersModal(): boolean {
    if (!this.userDetails) return false;

    return BillingUtils.isUsersTrialExpired(this.userDetails, 30) && !this.userDetails.Suspended;
  }

  private shouldShowExtendTrialModal(): boolean {
    return this.queryParams?.extendtrial && !this.userDetails?.Suspended;
  }

  private shouldShowExpiredTrialModal(): boolean {
    if (!this.userDetails) return false;

    const isSubuser = (this.userDetails?.Roles || []).includes(ApplicationRoles.Subuser);

    return !isSubuser && !this.userDetails?.Suspended && BillingUtils.isUsersTrialExpired(this.userDetails, 1);
  }

  /**
   * Checks if account disabled modal should be displayed due to negative balance.
   * Shows modal for paid accounts (non-FreeTrial) that have negative balance and disabled account status.
   * @description Validates user details to determine if account disabled modal should be shown to the user.
   * The modal is displayed only to paid accounts (non-FreeTrial) that have their account disabled.
   * @returns {boolean} Returns true if ALL conditions met:
   * - User details exist
   * - User has billing type other than FreeTrial (BillingType enum)
   * - User account is disabled (AccountDisabled flag)
   * @private
   */
  private shouldShowNegativeBalanceModal(): boolean {
    return !this.userDetails ? false : this.userDetails?.BillingType !== BillingType.FreeTrial && this.userDetails?.AccountDisabled;
  }

  private shouldShowPermissionsModal(): boolean {
    const roles = this.userDetails?.Roles || [];
    const hasOutdatedRoles = !!OutdatedApplicationRoles.filter((role) => roles.includes(role)).length;

    // Return true if a subuser and has any of the old roles and no new one (ManageZones)
    return roles.includes(ApplicationRoles.Subuser) && !roles.includes(ApplicationRoles.SubuserManage) && hasOutdatedRoles;
  }

  private showPermissionsModal(): void {
    const dialog = this.dialog.show(PermissionsUpdateModalComponent, {
      id: 'permissionsModal',
      class: 'modal-md modal-dialog-centered',
    });

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.dialog.hide('permissionsModal');
        this.#anyModalShown.set(!!this.dialog.getModalsCount());
        this.handleShowModals();
      }),
    );
  }

  private showExtendTrialModal(extendTrial: string): void {
    const dialog = this.dialog.show(ExtendTrialModalComponent, {
      id: 'extendTrialModal',
      class: 'modal-sm modal-md-xl modal-dialog-centered',
      initialState: {
        data: {
          extra: {
            isAuto: extendTrial === 'auto',
          },
        },
      },
    });

    this.subscriptions.add(
      dialog.content?.getOnAction$().subscribe((event: ClickActionEvent) => {
        if (event.action === 'confirm') {
          this.showModalsQue['2_expiredTrialModal'] = false;
        }
      }),
    );

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.dialog.hide('extendTrialModal');
        this.#anyModalShown.set(!!this.dialog.getModalsCount());
        this.handleShowModals();

        this.router.navigate([], {
          queryParams: {
            extendtrial: null,
          },
          queryParamsHandling: 'merge',
        });
      }),
    );
  }

  private showExtendTrialAllUsersModal(): void {
    const dialog = this.dialog.show(ExtendTrialAllUsersModalComponent, {
      id: 'extendTrialAllUsersModal',
      class: 'modal-sm modal-md-xl modal-dialog-centered',
    });

    this.subscriptions.add(
      dialog.content?.getOnAction$().subscribe((event: ClickActionEvent) => {
        if (event.action === 'confirm') {
          this.showModalsQue['2_expiredTrialModal'] = false;
        }
      }),
    );

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.dialog.hide('extendTrialAllUsersModal');
        this.#anyModalShown.set(!!this.dialog.getModalsCount());
        this.handleShowModals();
      }),
    );
  }

  private showExpiredTrialModal(): void {
    const dialog = this.dialog.show(ExpiredTrialModalComponent, {
      id: 'expiredTrialModal',
      class: 'modal-sm modal-md-xl modal-dialog-centered',
    });

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.dialog.hide('expiredTrialModal');
        this.#anyModalShown.set(!!this.dialog.getModalsCount());
        this.handleShowModals();

        if (!this.dialog.getModalsCount()) this.dialog.removeBackdrop(); // Remove backdrop if needed
      }),
    );
  }

  private saveUtmDataToCookies(): void {
    this.subscriptions.add(
      this.route.queryParams.subscribe((params) => {
        const cookieOptions: CookieOptions = {
          domain: window.location.hostname,
          expires: DateTimeUtils.dayjsNow().add(1, 'year').format(),
        };

        if (params.gclid) {
          this.cookieService.put('gclid', params.gclid, cookieOptions);
        }

        if (params.twclid) {
          this.cookieService.put('twclid', params.twclid, cookieOptions);
        }

        if (params.rdt_cid) {
          this.cookieService.put('rdt_cid', params.rdt_cid, cookieOptions);
        }

        if (params.msclkid) {
          this.cookieService.put('msclkid', params.msclkid, cookieOptions);
        }

        if (params.fbclid) {
          this.cookieService.put('fbclid', params.fbclid, cookieOptions);
        }

        if (params.liclid) {
          this.cookieService.put('liclid', params.liclid, cookieOptions);
        }

        if (params.pk_campaign) {
          this.cookieService.put('pk_campaign', params.pk_campaign, cookieOptions);
        }

        if (params.pk_keyword) {
          this.cookieService.put('pk_keyword', params.pk_keyword, cookieOptions);
        }

        if (params.device) {
          this.cookieService.put('device', params.device, cookieOptions);
        }

        if (params.pk_medium) {
          this.cookieService.put('pk_medium', params.pk_medium, cookieOptions);
        }

        if (params.pk_source) {
          this.cookieService.put('pk_source', params.pk_source, cookieOptions);
        }

        if (params.pk_cid) {
          this.cookieService.put('pk_cid', params.pk_cid, cookieOptions);
        }

        if (params.pk_buttonurl) {
          this.cookieService.put('pk_buttonurl', params.pk_buttonurl, cookieOptions);
        }

        if (params.pk_buttonlocation) {
          this.cookieService.put('pk_buttonlocation', params.pk_buttonlocation, cookieOptions);
        }

        if (params.bunnyab) {
          this.cookieService.put('bunnyab', params.bunnyab, cookieOptions);
        }

        if (params.ref_domain) {
          this.cookieService.put('ref_domain', params.ref_domain, cookieOptions);
        }
        if (params.pk_ad) {
          this.cookieService.put('pk_ad', params.pk_ad, cookieOptions);
        }
        if (params.pk_copy) {
          this.cookieService.put('pk_copy', params.pk_copy, cookieOptions);
        }
        if (params.pk_url) {
          this.cookieService.put('pk_url', params.pk_url, cookieOptions);
        }
        if (params.pk_placement) {
          this.cookieService.put('pk_placement', params.pk_placement, cookieOptions);
        }
        if (params.pk_platform) {
          this.cookieService.put('pk_platform', params.pk_platform, cookieOptions);
        }
      }),
    );
  }

  public hideLoadingScreen(): void {
    const loadingScreen = document.getElementById('bnLoadingScreen');
    if (loadingScreen) loadingScreen.style.display = 'none';
  }

  private handleShowModals(): void {
    Object.keys(this.showModalsQue).map((modal) => {
      if (this.showModalsQue[modal] && !this.#anyModalShown()) {
        if (modal === '0_extendTrialModal') {
          this.showExtendTrialModal(this.queryParams?.extendtrial);
          this.showModalsQue['0_extendTrialModal'] = false;
        }

        if (modal === '1_extendTrialAllUsersModal') {
          this.showExtendTrialAllUsersModal();
          this.showModalsQue['1_extendTrialAllUsersModal'] = false;
        }

        if (modal === '2_expiredTrialModal') {
          this.showExpiredTrialModal();
          this.showModalsQue['2_expiredTrialModal'] = false;
        }

        if (modal === '3_permissionsModal') {
          this.showPermissionsModal();
          this.showModalsQue['3_permissionsModal'] = false;
        }

        if (modal === '4_userDisabled') {
          this.showAccountDisabledModal();
          this.showModalsQue['4_userDisabled'] = false;
        }
      }
    });

    this.#anyModalShown.set(!!this.dialog.getModalsCount());
  }

  private showAccountDisabledModal(): void {
    const dialog = this.dialog.show(UserDisabledModalComponent, {
      id: 'userAccountDisabledModal',
      class: 'modal-sm modal-md-xl modal-dialog-centered',
    });

    this.subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.dialog.hide('userAccountDisabledModal');
        this.#anyModalShown.set(!!this.dialog.getModalsCount());
        this.handleShowModals();
      }),
    );
  }

  private listenToAnyModalShown(): void {
    effect(() => {
      if (!this.#anyModalShown()) {
        this.dialog.removeBackdrop(); // Remove backdrop when count is 0
      }
    });
  }
}
